import React, { useState, useEffect } from 'react';
import { FaUsers, FaStore, FaLayerGroup, FaArrowUp, FaDownload, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
// 1. Import the Auth Hook
import { useAdminAuth } from '../context/AdminAuthContext';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  // 2. Get the name from the hook
  const { name } = useAdminAuth(); 

  const [counts, setCounts] = useState({
    buyers: 0,
    sellers: 0,
    categories: 0,
    issues: 0
  });
  const [recentSellers, setRecentSellers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Mock data for the Wavy Area Chart
  const chartData = [
    { name: 'Jan', buyers: 300, sellers: 150 },
    { name: 'Feb', buyers: 450, sellers: 230 },
    { name: 'Mar', buyers: 320, sellers: 480 },
    { name: 'Apr', buyers: 500, sellers: 390 },
    { name: 'May', buyers: 420, sellers: 580 },
    { name: 'Jun', buyers: 550, sellers: 460 },
    { name: 'Jul', buyers: 600, sellers: 520 },
    { name: 'Aug', buyers: 580, sellers: 610 },
    { name: 'Sep', buyers: 650, sellers: 550 },
    { name: 'Oct', buyers: 700, sellers: 680 },
    { name: 'Nov', buyers: 620, sellers: 750 },
    { name: 'Dec', buyers: 750, sellers: 800 },
  ];

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const resBuyers = await fetch('/springboot/api/admin/buyers');
      const resSellers = await fetch('/springboot/api/admin/sellers-activity');
      const resCats = await fetch('/nodejs/api/categories');

      const buyersData = resBuyers.ok ? await resBuyers.json() : [];
      const sellersData = resSellers.ok ? await resSellers.json() : [];
      const catsData = resCats.ok ? await resCats.json() : [];

      const countAll = (list: any[]): number => {
        let count = list.length;
        list.forEach(cat => {
          if (cat.subCategories?.length > 0) count += countAll(cat.subCategories);
        });
        return count;
      };

      setCounts({
        buyers: buyersData.length,
        sellers: sellersData.length,
        categories: countAll(catsData),
        issues: 0
      });

      setRecentSellers(sellersData.slice(0, 5));

    } catch (err) {
      console.error("Dashboard Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  return (
    <div className="space-y-6 lg:space-y-8 pb-10">
      
      {/* Top Section: Welcome Banner + Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        
        {/* 1. Large Welcome Card */}
        <div className="lg:col-span-2 bg-[#e0f2f1] rounded-[30px] p-6 lg:p-8 relative overflow-hidden flex flex-col justify-center min-h-[240px]">
          <div className="relative z-10 max-w-[80%] lg:max-w-full">
            <h2 className="text-2xl lg:text-3xl font-black text-[#004d40] mb-2 leading-tight">
              Welcome back,<br className="sm:hidden" /> {name || "Admin"}!
            </h2>
            <p className="text-[#00695c] font-medium mb-6 max-w-md text-sm lg:text-base">
              Check your platform activity. You have <span className="font-bold text-[#004d40]">{counts.sellers}</span> new sellers waiting for approval this week.
            </p>
            <button 
              onClick={() => navigate('/frog-side/sellers')}
              className="bg-[#006d5b] text-white px-6 py-3 rounded-xl font-bold text-xs lg:text-sm shadow-lg shadow-[#006d5b]/20 hover:scale-105 transition-transform"
            >
              Check Dashboard
            </button>
            
            {/* Image adjusted for mobile: smaller & semi-transparent so text remains readable */}
            <img 
              src="https://chanchallodhi.com/wp-content/uploads/2025/04/img1.png" 
              alt="Welcome Illustration" 
              className="absolute bottom-0 right-0 w-32 lg:w-52 select-none pointer-events-none opacity-50 lg:opacity-100 transition-opacity"
            />
          </div>
          {/* Decorative Background */}
          <div className="absolute -right-10 -bottom-20 w-64 h-64 bg-[#006d5b]/10 rounded-full blur-3xl"></div>
          <div className="absolute right-10 top-10 w-32 h-32 bg-[#26a69a]/10 rounded-full blur-2xl"></div>
        </div>

        {/* 2. Side Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
          
          {/* Total Users Card */}
          <div className="bg-[#006d5b] rounded-[30px] p-6 text-white shadow-xl shadow-[#006d5b]/20 relative overflow-hidden group cursor-pointer hover:-translate-y-1 transition-transform" onClick={() => navigate('/frog-side/users')}>
             <div className="flex justify-between items-start">
               <div>
                 <p className="text-emerald-100 text-[10px] lg:text-xs font-bold uppercase tracking-widest mb-1">Total Buyers</p>
                 <h3 className="text-3xl lg:text-4xl font-black">{counts.buyers}</h3>
               </div>
               <div className="p-3 bg-white/20 rounded-full backdrop-blur-sm">
                 <FaUsers className="text-white" size={20} />
               </div>
             </div>
             <p className="mt-4 text-xs font-medium text-emerald-100 flex items-center gap-1">
               <FaArrowUp /> +12% from last month
             </p>
          </div>

          {/* Total Categories Card */}
          <div className="bg-white rounded-[30px] p-6 border border-gray-100 shadow-sm relative group cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1" onClick={() => navigate('/frog-side/category-list')}>
             <div className="flex justify-between items-start">
               <div>
                 <p className="text-gray-400 text-[10px] lg:text-xs font-bold uppercase tracking-widest mb-1">Categories</p>
                 <h3 className="text-3xl lg:text-4xl font-black text-gray-800">{counts.categories}</h3>
               </div>
               <div className="p-3 bg-orange-50 text-orange-500 rounded-full">
                 <FaLayerGroup size={20} />
               </div>
             </div>
             <div className="mt-4 w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                <div className="bg-orange-500 h-full w-[70%]"></div>
             </div>
          </div>
        </div>
      </div>

      {/* Middle Section: Chart + Revenue Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        
        {/* 3. The WAVY AREA CHART */}
        <div className="lg:col-span-2 bg-white rounded-[30px] p-6 lg:p-8 shadow-sm border border-gray-100 flex flex-col">
           <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 lg:mb-8 gap-4">
             <div>
               <h3 className="text-lg lg:text-xl font-black text-gray-900">Revenue Updates</h3>
               <p className="text-sm text-gray-400 font-medium">Overview of Profit</p>
             </div>
             <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1 text-xs font-bold text-gray-500">
                    <span className="w-2 h-2 rounded-full bg-[#006d5b]"></span> Buyers
                </span>
                <span className="flex items-center gap-1 text-xs font-bold text-gray-500">
                    <span className="w-2 h-2 rounded-full bg-[#4db6ac]"></span> Sellers
                </span>
                <div className="ml-2 px-3 py-1 bg-gray-50 rounded-full text-xs font-bold text-gray-500 border border-gray-100 flex items-center gap-2 cursor-pointer hover:bg-gray-100">
                    <FaCalendarAlt /> 2026 <FaArrowUp size={10}/>
                </div>
             </div>
           </div>
           
           <div className="h-[300px] w-full">
             <ResponsiveContainer width="100%" height="100%">
               <AreaChart data={chartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                 <defs>
                   <linearGradient id="colorBuyers" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="5%" stopColor="#006d5b" stopOpacity={0.6}/>
                     <stop offset="95%" stopColor="#006d5b" stopOpacity={0}/>
                   </linearGradient>
                   <linearGradient id="colorSellers" x1="0" y1="0" x2="0" y2="1">
                     <stop offset="5%" stopColor="#4db6ac" stopOpacity={0.6}/>
                     <stop offset="95%" stopColor="#4db6ac" stopOpacity={0}/>
                   </linearGradient>
                 </defs>
                 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                 <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 10, fontWeight: 'bold' }} 
                    dy={10}
                 />
                 <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#9ca3af', fontSize: 10, fontWeight: 'bold' }} 
                 />
                 <Tooltip 
                    contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 30px rgba(0,0,0,0.08)', padding: '12px' }}
                    itemStyle={{ fontSize: '12px', fontWeight: 'bold', color: '#374151' }}
                    labelStyle={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}
                 />
                 <Area 
                    type="monotone" 
                    dataKey="sellers" 
                    stroke="#4db6ac" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorSellers)" 
                    name="Sellers"
                 />
                 <Area 
                    type="monotone" 
                    dataKey="buyers" 
                    stroke="#006d5b" 
                    strokeWidth={3}
                    fillOpacity={1} 
                    fill="url(#colorBuyers)" 
                    name="Buyers"
                 />
               </AreaChart>
             </ResponsiveContainer>
           </div>
        </div>

        {/* 4. Right Side Summary / Quick Report */}
        <div className="bg-[#1a1c1e] text-white rounded-[30px] p-8 shadow-xl flex flex-col justify-between relative overflow-hidden min-h-[350px]">
           <div className="relative z-10">
              <div className="w-12 h-12 bg-[#006d5b] rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg shadow-[#006d5b]/30">
                <FaStore size={22} />
              </div>
              <h3 className="text-4xl font-light mb-1">{counts.sellers}</h3>
              <p className="text-gray-400 font-bold text-[10px] uppercase tracking-widest mb-8">Active Sellers</p>
              
              <div className="space-y-5">
                <div className="flex justify-between items-center text-sm border-b border-gray-800 pb-3">
                  <span className="text-gray-400 font-medium">Verified</span>
                  <span className="font-bold text-[#006d5b]">+45%</span>
                </div>
                <div className="flex justify-between items-center text-sm border-b border-gray-800 pb-3">
                  <span className="text-gray-400 font-medium">Pending</span>
                  <span className="font-bold text-yellow-500">12</span>
                </div>
              </div>
           </div>
           
           <button className="relative z-10 mt-6 w-full py-4 bg-[#006d5b] hover:bg-[#005a4b] rounded-2xl font-bold text-xs tracking-widest transition-colors flex items-center justify-center gap-2 group">
             <FaDownload className="group-hover:-translate-y-0.5 transition-transform" /> DOWNLOAD REPORT
           </button>

           <div className="absolute top-0 right-0 w-40 h-40 bg-[#006d5b]/20 rounded-bl-[100px]"></div>
           <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-[#000] to-transparent opacity-20 pointer-events-none"></div>
        </div>
      </div>

      {/* Bottom Section: Recent Sellers Table */}
      <div className="bg-white rounded-[30px] p-6 lg:p-8 shadow-sm border border-gray-100">
         <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-6 gap-2">
            <div>
               <h3 className="text-lg lg:text-xl font-black text-gray-900">Recent Sellers</h3>
               <p className="text-sm text-gray-400 font-medium mt-1">New partners waiting for onboarding</p>
            </div>
            <button onClick={() => navigate('/frog-side/users')} className="text-[#006d5b] font-bold text-sm hover:underline">View All</button>
         </div>
         
         {/* Responsive Table Wrapper - Enable Horizontal Scroll on Mobile */}
         <div className="overflow-x-auto w-full">
           <table className="w-full text-left border-collapse min-w-[700px]"> {/* Fixed Min Width to ensure scroll */}
             <thead>
               <tr className="text-[11px] text-gray-400 uppercase tracking-wider border-b border-gray-100">
                 <th className="py-4 font-bold pl-4">Seller Profile</th>
                 <th className="py-4 font-bold">Status</th>
                 <th className="py-4 font-bold">Earnings</th>
                 <th className="py-4 font-bold text-right pr-4">Action</th>
               </tr>
             </thead>
             <tbody className="text-sm text-gray-700">
               {recentSellers.length > 0 ? recentSellers.map((seller, i) => (
                 <tr key={i} className="group hover:bg-gray-50/50 transition-colors border-b border-gray-50 last:border-0">
                   <td className="py-4 pl-4">
                     <div className="flex items-center gap-3">
                       <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center font-bold text-gray-500 shadow-sm">
                         {seller.companyName ? seller.companyName.charAt(0) : "U"}
                       </div>
                       <div>
                         <p className="font-bold text-gray-900">{seller.companyName || "N/A"}</p>
                         <p className="text-xs text-gray-400">{seller.username}</p>
                       </div>
                     </div>
                   </td>
                   <td className="py-4">
                     <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${seller.isVerified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                       {seller.isVerified ? 'Active' : 'Pending'}
                     </span>
                   </td>
                   <td className="py-4 font-bold text-gray-900">$0.00</td>
                   <td className="py-4 text-right pr-4">
                     <button className="text-gray-400 hover:text-[#006d5b] transition-colors font-bold text-xs">
                       Edit
                     </button>
                   </td>
                 </tr>
               )) : (
                 <tr>
                   <td colSpan={4} className="text-center py-8 text-gray-400 text-xs font-bold uppercase">No data available</td>
                 </tr>
               )}
             </tbody>
           </table>
         </div>
      </div>
    </div>
  );
};

export default AdminDashboard;